import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Home as HomeIcon, Leaf } from "lucide-react";
import { history, team, company, img } from "../mock";
import Seo from "../components/Seo";

const stats = [
  { icon: Award, value: "40+", label: "Years Building" },
  { icon: HomeIcon, value: "1,000+", label: "Homes Built" },
  { icon: Users, value: "3", label: "Generations" },
  { icon: Leaf, value: "10-Yr", label: "Structural Warranty" }
];

const WhoWeAre = () => {
  useEffect(() => {
    const obs = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.1 });
    document.querySelectorAll(".mh-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      <Seo
        title="Who We Are"
        description="Meet Monarch Homes of Brevard - three generations of homebuilders led by Ed and Brian Fleis, building custom homes on Florida's Space Coast since 1980."
        keywords="Monarch Homes of Brevard, Ed Fleis, Brian Fleis, Brevard County home builder history"
        path="/who-we-are"
      />
      <section className="mh-page-hero" style={{ backgroundImage: `url(${img.coastal1})` }}>
        <div className="mh-page-hero-inner">
          <div className="mh-crumb"><Link to="/">Home</Link> / Who We Are</div>
          <h1>Who We Are</h1>
        </div>
      </section>

      {/* History */}
      <section className="mh-section">
        <div className="mh-container mh-split">
          <div className="mh-reveal">
            <img src={img.team} alt="Ed and Brian Fleis, Monarch Homes of Brevard" />
          </div>
          <div className="mh-reveal">
            <div className="mh-eyebrow">Our Story</div>
            <h2>{history.heading}</h2>
            {history.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mh-section navy tight">
        <div className="mh-container">
          <div className="mh-highlights">
            {stats.map((s, i) => (
              <div key={i} className="mh-reveal" style={{ textAlign: "center", transitionDelay: `${i * 70}ms` }}>
                <div className="mh-hi-icon" style={{ background: "rgba(255,255,255,.08)", color: "var(--amber)" }}><s.icon size={28} /></div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 42, fontWeight: 700, color: "#fff" }}>{s.value}</div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: 1, textTransform: "uppercase", fontSize: 12.5, color: "#a9c0d3", marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovative designs */}
      <section className="mh-section cream">
        <div className="mh-container mh-split">
          <div className="mh-reveal">
            <div className="mh-eyebrow">Known For Innovative Designs</div>
            <h2>Thoughtful Spaces, Superior Craftsmanship</h2>
            <p>Monarch Homes takes pride in designing useful spaces in both large and small homes. Our floorplans incorporate many innovative and exciting designs to choose from - including the larger Estate Series and the smaller Cottage Series homes.</p>
            <p>Each home is designed with thoughtful use of square footage, aesthetic appeal and quality construction, with opportunities for the homebuyer to customize to their personal taste. By using only the finest quality materials and construction methods, a superior outcome is assured from the start.</p>
            <div style={{ marginTop: 26 }}>
              <Link to="/build-on-your-lot" className="mh-btn-outline">Explore Our Floor Plans</Link>
            </div>
          </div>
          <div className="mh-reveal">
            <img src={img.kitchen2} alt="Designer kitchen" style={{ width: "100%", borderRadius: 8, boxShadow: "0 22px 46px rgba(14,44,71,.16)" }} />
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="mh-section" id="staff">
        <div className="mh-container">
          <div className="mh-section-head mh-reveal">
            <div className="mh-eyebrow">Meet Our Staff</div>
            <h2>The Family Behind Monarch Homes</h2>
            <div className="mh-divider" />
          </div>
          <div className="mh-team-grid">
            {team.map((m, i) => (
              <div key={i} className="mh-team-card mh-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="mh-team-avatar">{m.name.charAt(0)}</div>
                <h3>{m.name}</h3>
                <div className="mh-team-role">{m.role}</div>
                <p>{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mh-cta-band">
        <div className="mh-container mh-reveal">
          <h2>It&rsquo;s Time To Find Your Dream Home</h2>
          <p>Our commitment is to provide personal attention and superior customer care during the designing and building process - and to deliver a home that is beautiful, energy efficient, and a great value.</p>
          <div className="mh-hero-actions">
            <Link to="/contact" className="mh-cta-btn">Get In Touch <ArrowRight size={16} /></Link>
            <a href={company.phoneHref} className="mh-btn-ghost-light">Call {company.phone}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
